import { Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { XIcon } from "@heroicons/react/outline";
import type { GameLocale } from "../../i18n/types";
import { getMessages } from "../../i18n/messages";

type Props = {
  isOpen: boolean;
  handleClose: () => void;
  gameStats: {
    gamesWon: number;
    gamesPlayed: number;
    currentStreak: number;
    maxStreak: number;
    winPercentage: number;
  };
  locale: GameLocale;
};

export const StatsModal = ({ isOpen, handleClose, gameStats, locale }: Props) => {
  const m = getMessages(locale);

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="fixed z-10 inset-0 overflow-y-auto"
        onClose={handleClose}
      >
        <div className="flex items-center justify-center min-h-screen p-4 text-center">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <div className="relative inline-block w-full max-w-sm bg-white dark:bg-gray-800 rounded-xl px-6 py-6 text-center shadow-2xl transform transition-all">
              <div className="absolute right-4 top-4">
                <button
                  type="button"
                  className="bg-white dark:bg-gray-800 rounded-full p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors"
                  onClick={handleClose}
                >
                  <span className="sr-only">{m.closeAria}</span>
                  <XIcon className="h-5 w-5" aria-hidden="true" />
                </button>
              </div>
              <div>
                <Dialog.Title
                  as="h3"
                  className="text-xl font-bold text-gray-900 dark:text-white mb-6"
                >
                  {m.statsTitle}
                </Dialog.Title>
                <div className="grid grid-cols-2 gap-6">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-gray-900 dark:text-white">
                      {gameStats.gamesPlayed}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      {m.statsPlayed}
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-gray-900 dark:text-white">
                      {gameStats.winPercentage}%
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      {m.statsWinPct}
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-gray-900 dark:text-white">
                      {gameStats.currentStreak}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      {m.statsCurrentStreak}
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-gray-900 dark:text-white">
                      {gameStats.maxStreak}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      {m.statsMaxStreak}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
};
